// 🔥 FIXED: auth-components.jsx - Complete signup invite integration
import React, { createContext, useContext, useState, useEffect, Suspense } from 'react';
import { logger } from "../../utils/logger";

// 🔧 FIXED: Change from static to dynamic import
// OLD: import MyAnalyses from '../dashboard/MyAnalyses';
const MyAnalyses = React.lazy(() => import('../dashboard/MyAnalyses'));

// Authentication Context
const AuthContext = createContext();

// Loading component for MyAnalyses
const MyAnalysesLoading = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
    fontSize: '1rem',
    color: '#6b7280',
    fontFamily: 'Inter, sans-serif'
  }}>
    <div style={{
      animation: 'spin 1s linear infinite',
      width: '24px',
      height: '24px',
      border: '2px solid #f3f4f6',
      borderTop: '2px solid #257980',
      borderRadius: '50%',
      marginRight: '12px'
    }}></div>
    Loading analyses...
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Authentication Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModal, setAuthModal] = useState({ isOpen: false, mode: 'login' });

  useEffect(() => {
    if (typeof window !== 'undefined' && window.firebase) {
      const unsubscribe = window.firebase.auth().onAuthStateChanged((user) => {
        if (user && !user.isAnonymous) {
          setUser({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || user.email?.split('@')[0],
            photoURL: user.photoURL,
            isAnonymous: false,
            emailVerified: user.emailVerified
          });
        } else {
          setUser(null);
        }
        setLoading(false);
      });

      return () => unsubscribe();
    } else {
      const timeout = setTimeout(() => {
        console.warn('Firebase not detected, proceeding without auth');
        setLoading(false);
      }, 2000);
      
      return () => clearTimeout(timeout);
    }
  }, []);

  // Loading screen
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        fontFamily: 'Inter, sans-serif',
        flexDirection: 'column',
        gap: '16px',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{
          fontSize: '32px',
          animation: 'pulse 2s ease-in-out infinite'
        }}>
          🏈
        </div>
        <div style={{
          color: '#374151',
          fontWeight: '500'
        }}>
          Loading...
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
        `}</style>
      </div>
    );
  }

  const signInWithEmail = async (email, password) => {
    try {
      if (!window.firebase || !window.firebase.auth) throw new Error('Auth not ready. Please refresh and try again.');
      const result = await window.firebase.auth().signInWithEmailAndPassword(email, password);
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const signUpWithEmail = async (email, password, displayName) => {
    try {
      if (!window.firebase || !window.firebase.auth) throw new Error('Auth not ready. Please refresh and try again.');
      const result = await window.firebase.auth().createUserWithEmailAndPassword(email, password);
      
      // Update display name
      if (displayName) {
        await result.user.updateProfile({ displayName });
      }
      
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const signInWithGoogle = async () => {
    try {
      if (!window.firebase || !window.firebase.auth) throw new Error('Auth not ready. Please refresh and try again.');
      const provider = new window.firebase.auth.GoogleAuthProvider();
      const result = await window.firebase.auth().signInWithPopup(provider);
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const signOut = async () => {
    try {
      if (!window.firebase || !window.firebase.auth) return { success: true };
      await window.firebase.auth().signOut();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // ✅ NEW: Forgot password function
  const sendPasswordReset = async (email) => {
    try {
      if (!window.firebase || !window.firebase.auth) throw new Error('Auth not ready. Please refresh and try again.');
      await window.firebase.auth().sendPasswordResetEmail(email);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    loading,
    authModal,
    setAuthModal,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    sendPasswordReset
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ✅ ENHANCED: Auth Modal Component with Complete Signup Invite Integration
const AuthModal = () => {
  const { authModal, setAuthModal, signInWithEmail, signUpWithEmail, signInWithGoogle, sendPasswordReset } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // 🆕 NEW: Signup invite integration
  const [signupInviteData, setSignupInviteData] = useState(null);

  // 🆕 NEW: Check for signup invite when modal opens
  useEffect(() => {
    if (authModal.isOpen && authModal.mode === 'signup') {
      try {
        const pendingInvite = localStorage.getItem('sbe_pending_invite');
        const isSignupInvite = localStorage.getItem('sbe_signup_invite_active');
        
        if (pendingInvite && isSignupInvite) {
          const inviteData = JSON.parse(pendingInvite);
          console.log('🎯 SIGNUP INVITE: Found pending invite for signup:', inviteData);
          setSignupInviteData(inviteData);
        }
      } catch (error) {
        console.error('❌ Error checking signup invite:', error);
      }
    }
  }, [authModal.isOpen, authModal.mode]);

  const handleClose = () => {
    setAuthModal({ isOpen: false, mode: 'login' });
    setFormData({ email: '', password: '', confirmPassword: '', displayName: '' });
    setError('');
    setResetEmailSent(false);
    setSignupInviteData(null); // 🆕 Clear signup invite data
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (authModal.mode === 'signup' && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      let result;
      if (authModal.mode === 'signup') {
        result = await signUpWithEmail(formData.email, formData.password, formData.displayName);
        
        // 🆕 NEW: Handle post-signup invite processing
        if (result.success && signupInviteData) {
          console.log('🎯 SIGNUP SUCCESS: Processing pending invite after account creation');
          // Don't clear the invite here - let the auth effect handle it
        }
      } else {
        result = await signInWithEmail(formData.email, formData.password);
      }
      
      if (!result.success) throw new Error(result.error);

      console.log('🎯 AUTH SUCCESS - About to close modal');
      handleClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await signInWithGoogle();
      
      // 🆕 NEW: Handle post-Google signup invite processing
      if (result.success && signupInviteData && authModal.mode === 'signup') {
        console.log('🎯 GOOGLE SIGNUP SUCCESS: Processing pending invite after account creation');
        // Don't clear the invite here - let the auth effect handle it
      }
      
      if (!result.success) throw new Error(result.error);
      handleClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Handle forgot password
  const handleForgotPassword = async () => {
    if (!formData.email) {
      setError('Please enter your email address first.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const result = await sendPasswordReset(formData.email);
      if (!result.success) throw new Error(result.error);
      
      setResetEmailSent(true);
      setError('');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!authModal.isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '400px',
        width: '100%',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        position: 'relative'
      }}>
        {/* Close Button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
        >
          ×
        </button>

        {/* 🆕 NEW: Signup invite banner */}
        {signupInviteData && authModal.mode === 'signup' && (
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '2px solid #0ea5e9',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '20px', marginBottom: '8px' }}>🎉</div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#0f172a',
              marginBottom: '4px'
            }}>
              You're joining a group!
            </div>
            <div style={{
              fontSize: '12px',
              color: '#64748b'
            }}>
              Create your account to join with invite code: <strong>{signupInviteData.inviteCode}</strong>
            </div>
          </div>
        )}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h2 style={{ 
            color: '#257980', 
            margin: '0 0 8px 0',
            fontSize: '24px',
            fontWeight: '600'
          }}>
            {authModal.mode === 'signup' ? 
              (signupInviteData ? 'Create Account to Join' : 'Create Account') : 
              'Welcome Back'
            }
          </h2>
          <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>
            {authModal.mode === 'signup' ? 
              (signupInviteData ? 'Join the group and start analyzing!' : 'Create your account to start analyzing football games!') : 
              'Sign in to access your saved analyses and results'
            }
          </p>
        </div>

        {/* ✅ NEW: Password reset success message */}
        {resetEmailSent && (
          <div style={{
            backgroundColor: '#d1fae5',
            color: '#065f46',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            border: '1px solid #10b981'
          }}>
            ✅ Password reset email sent! Check your inbox and follow the instructions to reset your password.
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            border: '1px solid #fecaca'
          }}>
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {authModal.mode === 'signup' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontWeight: '500',
                color: '#374151',
                fontSize: '14px'
              }}>
                Display Name
              </label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  color: '#111827',
                  backgroundColor: '#ffffff',
                  '-webkit-text-fill-color': '#111827'
                }}
                placeholder="Enter your name"
              />
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '500',
              color: '#374151',
              fontSize: '14px'
            }}>
              Email
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box',
                color: '#111827',
                backgroundColor: '#ffffff',
                '-webkit-text-fill-color': '#111827'
              }}
              placeholder="Enter your email"
            />
          </div>

          <div style={{ marginBottom: authModal.mode === 'signup' ? '16px' : '8px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '500',
              color: '#374151',
              fontSize: '14px'
            }}>
              Password
            </label>
            <input
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box',
                color: '#111827',
                backgroundColor: '#ffffff',
                '-webkit-text-fill-color': '#111827'
              }}
              placeholder="Enter your password"
              minLength={6}
            />
          </div>

          {/* ✅ NEW: Forgot Password Link */}
          {authModal.mode === 'login' && (
            <div style={{ marginBottom: '24px', textAlign: 'right' }}>
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={loading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#257980',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Forgot password?
              </button>
            </div>
          )}

          {authModal.mode === 'signup' && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontWeight: '500',
                color: '#374151',
                fontSize: '14px'
              }}>
                Confirm Password
              </label>
              <input
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  color: '#111827',
                  backgroundColor: '#ffffff',
                  '-webkit-text-fill-color': '#111827'
                }}
                placeholder="Confirm your password"
                minLength={6}
              />
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: loading ? '#9ca3af' : '#257980',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              marginBottom: '16px'
            }}
          >
            {/* 🆕 NEW: Enhanced button text for signup invites */}
            {loading ? 'Loading...' : (authModal.mode === 'signup' ? 
              (signupInviteData ? 'Create Account & Join Group' : 'Create Account') : 
              'Sign In'
            )}
          </button>
        </form>

        {/* Divider */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e7eb' }}></div>
          <span style={{ margin: '0 16px', color: '#6b7280', fontSize: '14px' }}>or</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e7eb' }}></div>
        </div>

        {/* Google Sign In */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: 'white',
            color: '#374151',
            border: '2px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <span>🔍</span>
          {/* 🆕 NEW: Enhanced Google button text for signup invites */}
          {signupInviteData ? 'Join Group with Google' : 'Continue with Google'}
        </button>

        {/* Switch Mode */}
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: '#6b7280', fontSize: '14px' }}>
            {authModal.mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}
          </span>
          <button
            onClick={() => {
              setAuthModal(prev => ({ 
                ...prev, 
                mode: prev.mode === 'signup' ? 'login' : 'signup' 
              }));
              setSignupInviteData(null); // Clear signup invite when switching modes
            }}
            style={{
              marginLeft: '4px',
              color: '#257980',
              background: 'none',
              border: 'none',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            {authModal.mode === 'signup' ? 'Sign In' : 'Sign Up'}
          </button>
        </div>
      </div>
    </div>
  );
};

// User Profile Component
const UserProfile = () => {
  const { user, signOut } = useAuth();
  
  if (!user) return null;

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '8px 16px',
      backgroundColor: '#f0f9ff',
      border: '1px solid #257980',
      borderRadius: '8px',
      marginBottom: '24px'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: 'var(--sbe-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: '600',
        fontSize: '16px'
      }}>
        {user.photoURL ? (
          <img 
            src={user.photoURL} 
            alt="Profile" 
            style={{ width: '100%', height: '100%', borderRadius: '50%' }}
          />
        ) : (
          user.displayName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '👤'
        )}
      </div>
      
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: '600', color: '#1f2937' }}>
          {user.displayName || 'User'}
        </div>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          {user.email}
          {!user.emailVerified && (
            <span style={{ color: '#f59e0b', marginLeft: '8px' }}>
              (Unverified)
            </span>
          )}
        </div>
      </div>
      
      <button
        onClick={handleSignOut}
        style={{
          padding: '6px 12px',
          backgroundColor: 'white',
          color: '#257980',
          border: '1px solid #257980',
          borderRadius: '6px',
          fontSize: '14px',
          cursor: 'pointer'
        }}
      >
        Sign Out
      </button>
    </div>
  );
};

// 🔧 FIXED: My Analyses Tab Component with Suspense wrapper
const MyAnalysesTab = () => (
  <Suspense fallback={<MyAnalysesLoading />}>
    <MyAnalyses />
  </Suspense>
);

export { useAuth, AuthProvider, AuthModal, UserProfile, MyAnalysesTab };